import React from 'react';
import { 
  Users, 
  Layers, 
  CheckCircle2, 
  BarChart3, 
  Calendar as CalendarIcon 
} from 'lucide-react';

interface FilterSelectGroupProps {
  filterAssignee: string;
  onFilterAssignee: (val: string) => void;
  allAssignees: string[];
  filterType: string;
  onFilterType: (val: string) => void;
  filterStatus: string;
  onFilterStatus: (val: string) => void;
  filterPriority: string;
  onFilterPriority: (val: string) => void;
  datePadding: number;
  onDatePadding: (val: number) => void;
}

export const FilterSelectGroup: React.FC<FilterSelectGroupProps> = ({
  filterAssignee,
  onFilterAssignee,
  allAssignees,
  filterType,
  onFilterType,
  filterStatus,
  onFilterStatus,
  filterPriority,
  onFilterPriority,
  datePadding,
  onDatePadding
}) => {
  return (
    <>
      {/* Assignee Filter */}
      <div 
        className="flex items-center gap-2 shrink-0"
        title="Aufgaben nach Teammitgliedern filtern"
      >
        <div className={`p-1.5 rounded-lg transition-all duration-300 relative ${filterAssignee !== 'all' ? 'bg-indigo-100 dark:bg-indigo-950 text-indigo-600 shadow-sm ring-1 ring-indigo-200 dark:ring-indigo-800' : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 group-hover:bg-indigo-50 dark:group-hover:bg-indigo-900/20 group-hover:text-indigo-600'}`}>
          <Users size={14} />
          {filterAssignee !== 'all' && <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-indigo-500 rounded-full border-2 border-white dark:border-slate-900 animate-pulse" />}
        </div>
        <select 
          value={filterAssignee}
          onChange={(e) => onFilterAssignee(e.target.value)}
          className={`bg-transparent text-[11px] font-bold border-none focus:ring-0 cursor-pointer transition-colors py-1 ${filterAssignee !== 'all' ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-700 dark:text-slate-200 hover:text-indigo-600'}`}
        >
          <option value="all" className="dark:bg-slate-900">Team</option>
          {allAssignees.map(a => (
            <option key={a} value={a} className="dark:bg-slate-900">{a}</option>
          ))}
        </select>
      </div>

      {/* Type Filter */}
      <div 
        className="flex items-center gap-2 shrink-0"
        title="Aufgaben nach Typ filtern (z. B. Meilenstein)"
      >
        <div className={`p-1.5 rounded-lg transition-all duration-300 relative ${filterType !== 'all' ? 'bg-indigo-100 dark:bg-indigo-950 text-indigo-600 shadow-sm ring-1 ring-indigo-200 dark:ring-indigo-800' : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 group-hover:bg-indigo-50 dark:group-hover:bg-indigo-900/20 group-hover:text-indigo-600'}`}>
          <Layers size={14} />
          {filterType !== 'all' && <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-indigo-500 rounded-full border-2 border-white dark:border-slate-900 animate-pulse" />}
        </div>
        <select 
          value={filterType}
          onChange={(e) => onFilterType(e.target.value)}
          className={`bg-transparent text-[11px] font-bold border-none focus:ring-0 cursor-pointer transition-colors py-1 ${filterType !== 'all' ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-700 dark:text-slate-200 hover:text-indigo-600'}`}
        >
          <option value="all" className="dark:bg-slate-900">Typen</option>
          <option value="task" className="dark:bg-slate-900">Aufgaben</option>
          <option value="milestone" className="dark:bg-slate-900">Meilensteine</option>
          <option value="deadline" className="dark:bg-slate-900">Deadlines</option>
          <option value="meeting" className="dark:bg-slate-900">Meetings</option>
          <option value="decision" className="dark:bg-slate-900">Entscheidungen</option>
          <option value="approval" className="dark:bg-slate-900">Freigaben</option>
        </select>
      </div>

      {/* Status Filter */}
      <div 
        className="flex items-center gap-2 shrink-0"
        title="Aufgaben nach Status filtern"
      >
        <div className={`p-1.5 rounded-lg transition-all duration-300 relative ${filterStatus !== 'all' ? 'bg-indigo-100 dark:bg-indigo-950 text-indigo-600 shadow-sm ring-1 ring-indigo-200 dark:ring-indigo-800' : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 group-hover:bg-indigo-50 dark:group-hover:bg-indigo-900/20 group-hover:text-indigo-600'}`}>
          <CheckCircle2 size={14} />
          {filterStatus !== 'all' && <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-indigo-500 rounded-full border-2 border-white dark:border-slate-900 animate-pulse" />}
        </div>
        <select 
          value={filterStatus}
          onChange={(e) => onFilterStatus(e.target.value)}
          className={`bg-transparent text-[11px] font-bold border-none focus:ring-0 cursor-pointer transition-colors py-1 ${filterStatus !== 'all' ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-700 dark:text-slate-200 hover:text-indigo-600'}`}
        >
          <option value="all" className="dark:bg-slate-900">Status</option>
          <option value="open" className="dark:bg-slate-900">Offen</option>
          <option value="timer" className="dark:bg-slate-900">Zeit (Aktiv/Erfasst)</option>
          <option value="hold" className="dark:bg-slate-900">Wartend</option>
          <option value="blocked" className="dark:bg-slate-900">Blockiert</option>
          <option value="completed" className="dark:bg-slate-900">Erledigt</option>
        </select>
      </div>

      {/* Priority Filter */}
      <div 
        className="flex items-center gap-2 shrink-0"
        title="Aufgaben nach Priorität filtern"
      >
        <div className={`p-1.5 rounded-lg transition-all duration-300 relative ${filterPriority !== 'all' ? 'bg-indigo-100 dark:bg-indigo-950 text-indigo-600 shadow-sm ring-1 ring-indigo-200 dark:ring-indigo-800' : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 group-hover:bg-indigo-50 dark:group-hover:bg-indigo-900/20 group-hover:text-indigo-600'}`}>
          <BarChart3 size={14} />
          {filterPriority !== 'all' && <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-indigo-500 rounded-full border-2 border-white dark:border-slate-900 animate-pulse" />}
        </div>
        <select 
          value={filterPriority}
          onChange={(e) => onFilterPriority(e.target.value)}
          className={`bg-transparent text-[11px] font-bold border-none focus:ring-0 cursor-pointer transition-colors py-1 ${filterPriority !== 'all' ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-700 dark:text-slate-200 hover:text-indigo-600'}`}
        >
          <option value="all" className="dark:bg-slate-900">Priorität</option>
          <option value="high" className="dark:bg-slate-900">Hoch</option>
          <option value="medium" className="dark:bg-slate-900">Mittel</option>
          <option value="low" className="dark:bg-slate-900">Niedrig</option>
          <option value="none" className="dark:bg-slate-900">Keine</option>
        </select>
      </div>

      {/* Date Padding */}
      <div 
        className="flex items-center gap-2"
        title="Zeigt zusätzliche leere Puffer-Tage vor und nach dem Projektzeitraum im Gantt-Chart an"
      >
        <div className={`p-1.5 rounded-lg transition-all duration-300 relative ${datePadding !== 3 ? 'bg-indigo-100 dark:bg-indigo-950 text-indigo-600 shadow-sm ring-1 ring-indigo-200 dark:ring-indigo-800' : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 group-hover:bg-indigo-50 dark:group-hover:bg-indigo-900/20 group-hover:text-indigo-600'}`}>
          <CalendarIcon size={14} />
          {datePadding !== 3 && <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-indigo-500 rounded-full border-2 border-white dark:border-slate-900 animate-pulse" />}
        </div>
        <div className="flex items-center gap-1.5">
          <input 
            type="number"
            min="0"
            max="365"
            value={datePadding}
            onChange={(e) => onDatePadding(parseInt(e.target.value) || 0)}
            className={`w-12 border rounded-lg px-2 py-1 text-xs font-bold focus:ring-2 outline-none transition-all ${datePadding !== 3 ? 'bg-indigo-50 dark:bg-indigo-900/30 border-indigo-200 dark:border-indigo-800 text-indigo-600 focus:ring-indigo-500/50' : 'bg-white/50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 focus:ring-indigo-500/50'}`}
          />
          <span className={`text-[10px] font-bold whitespace-nowrap transition-colors ${datePadding !== 3 ? 'text-indigo-600/70 dark:text-indigo-400/70' : 'text-slate-400 dark:text-slate-500'}`}>
            Tage Padding
          </span>
        </div>
      </div>
    </>
  );
};
